import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    TelegramLogo,
    WhatsappLogo,
    Envelope,
    Check,
    Copy,
    PaperPlaneTilt
} from "@phosphor-icons/react";

const SettingsPage = () => {
    const { user, setUser } = useAuth();
    const [telegramChatId, setTelegramChatId] = useState(user?.telegram_chat_id || "");
    const [whatsappId, setWhatsappId] = useState(user?.whatsapp_id || "");
    const [saving, setSaving] = useState(false);
    const [testMessage, setTestMessage] = useState("Mensaje de prueba desde Gestión de Obras - THC");
    const [testEmail, setTestEmail] = useState(user?.email || "");
    const [sendingTest, setSendingTest] = useState(false);

    const handleSaveTelegram = async () => {
        setSaving(true);
        try {
            await axios.post(`${API}/settings/telegram`, {
                chat_id: telegramChatId
            });
            toast.success("Configuración de Telegram guardada");
            // Update local user state
            setUser({ ...user, telegram_chat_id: telegramChatId });
        } catch (error) {
            toast.error("Error al guardar");
        } finally {
            setSaving(false);
        }
    };

    const handleTestTelegram = async () => {
        if (!telegramChatId) {
            toast.error("Primero configura tu Chat ID de Telegram");
            return;
        }
        setSendingTest(true);
        try {
            await axios.post(`${API}/notifications/send`, {
                type: "telegram",
                recipient: telegramChatId,
                message: testMessage
            });
            toast.success("Mensaje enviado a Telegram");
        } catch (error) {
            toast.error("Error al enviar mensaje");
        } finally {
            setSendingTest(false);
        }
    };

    const handleSaveWhatsapp = async () => {
        setSaving(true);
        try {
            await axios.post(`${API}/settings/whatsapp`, {
                phone: whatsappId
            });
            toast.success("Configuración de WhatsApp guardada");
            setUser({ ...user, whatsapp_id: whatsappId });
        } catch (error) {
            toast.error("Error al guardar");
        } finally {
            setSaving(false);
        }
    };

    const handleTestWhatsapp = async () => {
        if (!whatsappId) {
            toast.error("Primero configura tu número de WhatsApp");
            return;
        }
        setSendingTest(true);
        try {
            await axios.post(`${API}/notifications/send`, {
                type: "whatsapp",
                recipient: whatsappId,
                message: testMessage
            });
            toast.success("Mensaje enviado a WhatsApp");
        } catch (error) {
            toast.error("Error al enviar mensaje");
        } finally {
            setSendingTest(false);
        }
    };

    const handleTestEmail = async () => {
        if (!testEmail) {
            toast.error("Introduce un email");
            return;
        }
        setSendingTest(true);
        try {
            await axios.post(`${API}/notifications/send`, {
                type: "email",
                recipient: testEmail,
                subject: "Prueba de Gestión de Obras - THC",
                message: testMessage
            });
            toast.success("Email enviado");
        } catch (error) {
            toast.error("Error al enviar email. Verifica que la API key de Resend esté configurada.");
        } finally {
            setSendingTest(false);
        }
    };

    const copyBotCommand = () => {
        navigator.clipboard.writeText("/start");
        toast.success("Comando copiado");
    };

    return (
        <div className="space-y-6 animate-in" data-testid="settings-page">
            <div>
                <h1 className="font-heading font-bold text-2xl md:text-3xl">Configuración</h1>
                <p className="text-[#555555] mt-1">
                    Configura las integraciones de mensajería
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Telegram Section */}
                <div className="bg-white border border-[#E5E5E5] rounded-sm">
                    <div className="p-4 border-b border-[#E5E5E5] flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#0088CC] rounded-sm flex items-center justify-center">
                            <TelegramLogo size={24} weight="fill" className="text-white" />
                        </div>
                        <div>
                            <h2 className="font-heading font-bold">Telegram</h2>
                            <p className="text-xs text-[#555555]">Recibe notificaciones y consulta información</p>
                        </div>
                    </div>
                    <div className="p-6 space-y-4">
                        <div className="bg-[#F8F9FA] p-4 rounded-sm">
                            <h3 className="font-semibold text-sm mb-2">Cómo configurar:</h3>
                            <ol className="text-sm text-[#555555] space-y-2 list-decimal list-inside">
                                <li>Busca nuestro bot en Telegram: <code className="bg-white px-2 py-0.5 rounded text-[#0055FF]">@BuildManagerBot</code></li>
                                <li>Envía el comando <code className="bg-white px-2 py-0.5 rounded">/start</code></li>
                                <li>El bot te responderá con tu Chat ID</li>
                                <li>Copia el Chat ID y pégalo abajo</li>
                            </ol>
                        </div>

                        <div>
                            <Label className="label-overline mb-2 block">Tu Chat ID</Label>
                            <div className="flex gap-2">
                                <Input
                                    value={telegramChatId}
                                    onChange={(e) => setTelegramChatId(e.target.value)}
                                    className="input-industrial"
                                    placeholder="Ej: 123456789"
                                    data-testid="telegram-chatid-input"
                                />
                                <Button
                                    onClick={handleSaveTelegram}
                                    disabled={saving}
                                    className="btn-primary"
                                    data-testid="save-telegram-btn"
                                >
                                    {saving ? "..." : <Check size={20} />}
                                </Button>
                            </div>
                        </div>

                        <div className="pt-4 border-t border-[#E5E5E5]">
                            <h3 className="font-semibold text-sm mb-3">Comandos disponibles:</h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between items-center py-2 px-3 bg-[#F8F9FA] rounded-sm">
                                    <code>/stock</code>
                                    <span className="text-[#555555]">Ver stock bajo</span>
                                </div>
                                <div className="flex justify-between items-center py-2 px-3 bg-[#F8F9FA] rounded-sm">
                                    <code>/obras</code>
                                    <span className="text-[#555555]">Obras activas</span>
                                </div>
                                <div className="flex justify-between items-center py-2 px-3 bg-[#F8F9FA] rounded-sm">
                                    <code>/vehiculos</code>
                                    <span className="text-[#555555]">Estado vehículos</span>
                                </div>
                                <div className="flex justify-between items-center py-2 px-3 bg-[#F8F9FA] rounded-sm">
                                    <code>/tareas</code>
                                    <span className="text-[#555555]">Tareas pendientes</span>
                                </div>
                            </div>
                        </div>

                        <div className="pt-4 border-t border-[#E5E5E5]">
                            <Label className="label-overline mb-2 block">Probar notificación</Label>
                            <Textarea
                                value={testMessage}
                                onChange={(e) => setTestMessage(e.target.value)}
                                className="input-industrial mb-2"
                                rows={2}
                                data-testid="test-message-input"
                            />
                            <Button
                                onClick={handleTestTelegram}
                                disabled={sendingTest || !telegramChatId}
                                className="btn-secondary w-full"
                                data-testid="test-telegram-btn"
                            >
                                <PaperPlaneTilt size={18} className="mr-2" />
                                {sendingTest ? "Enviando..." : "Enviar mensaje de prueba"}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* WhatsApp Section */}
                <div className="bg-white border border-[#E5E5E5] rounded-sm">
                    <div className="p-4 border-b border-[#E5E5E5] flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#25D366] rounded-sm flex items-center justify-center">
                            <WhatsappLogo size={24} weight="fill" className="text-white" />
                        </div>
                        <div>
                            <h2 className="font-heading font-bold">WhatsApp</h2>
                            <p className="text-xs text-[#555555]">Recibe notificaciones y consulta información</p>
                        </div>
                    </div>
                    <div className="p-6 space-y-4">
                        <div className="bg-[#F8F9FA] p-4 rounded-sm">
                            <h3 className="font-semibold text-sm mb-2">Cómo configurar:</h3>
                            <ol className="text-sm text-[#555555] space-y-2 list-decimal list-inside">
                                <li>Escribe una vez al número de WhatsApp de THC</li>
                                <li>Introduce tu número abajo en formato internacional, sin "+" ni espacios (ej: 34600111222)</li>
                                <li>Guarda y ya podrás hablar con el bot por WhatsApp</li>
                            </ol>
                        </div>

                        <div>
                            <Label className="label-overline mb-2 block">Tu número de WhatsApp</Label>
                            <div className="flex gap-2">
                                <Input
                                    value={whatsappId}
                                    onChange={(e) => setWhatsappId(e.target.value)}
                                    className="input-industrial"
                                    placeholder="Ej: 34600111222"
                                    data-testid="whatsapp-phone-input"
                                />
                                <Button
                                    onClick={handleSaveWhatsapp}
                                    disabled={saving}
                                    className="btn-primary"
                                    data-testid="save-whatsapp-btn"
                                >
                                    {saving ? "..." : <Check size={20} />}
                                </Button>
                            </div>
                        </div>

                        <div className="pt-4 border-t border-[#E5E5E5]">
                            <Label className="label-overline mb-2 block">Probar notificación</Label>
                            <Textarea
                                value={testMessage}
                                onChange={(e) => setTestMessage(e.target.value)}
                                className="input-industrial mb-2"
                                rows={2}
                            />
                            <Button
                                onClick={handleTestWhatsapp}
                                disabled={sendingTest || !whatsappId}
                                className="btn-secondary w-full"
                                data-testid="test-whatsapp-btn"
                            >
                                <PaperPlaneTilt size={18} className="mr-2" />
                                {sendingTest ? "Enviando..." : "Enviar mensaje de prueba"}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Email Section */}
                <div className="bg-white border border-[#E5E5E5] rounded-sm">
                    <div className="p-4 border-b border-[#E5E5E5] flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#EA4335] rounded-sm flex items-center justify-center">
                            <Envelope size={24} weight="fill" className="text-white" />
                        </div>
                        <div>
                            <h2 className="font-heading font-bold">Email</h2>
                            <p className="text-xs text-[#555555]">Notificaciones por correo electrónico</p>
                        </div>
                    </div>
                    <div className="p-6 space-y-4">
                        <div className="bg-[#F8F9FA] p-4 rounded-sm">
                            <p className="text-sm text-[#555555]">
                                Las notificaciones por email se envían automáticamente a tu dirección registrada cuando hay alertas importantes como stock bajo o cambios en las tareas.
                            </p>
                        </div>

                        <div>
                            <Label className="label-overline mb-2 block">Tu email</Label>
                            <Input
                                value={user?.email || ""}
                                className="input-industrial"
                                disabled
                                data-testid="email-display"
                            />
                        </div>

                        <div className="pt-4 border-t border-[#E5E5E5]">
                            <Label className="label-overline mb-2 block">Probar email</Label>
                            <Input
                                type="email"
                                value={testEmail}
                                onChange={(e) => setTestEmail(e.target.value)}
                                className="input-industrial mb-2"
                                placeholder="Email de prueba"
                                data-testid="test-email-input"
                            />
                            <Button
                                onClick={handleTestEmail}
                                disabled={sendingTest}
                                className="btn-secondary w-full"
                                data-testid="test-email-btn"
                            >
                                <Envelope size={18} className="mr-2" />
                                {sendingTest ? "Enviando..." : "Enviar email de prueba"}
                            </Button>
                        </div>

                        <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-sm">
                            <p className="text-sm text-yellow-800">
                                <strong>Nota:</strong> Para que los emails funcionen, asegúrate de que la API Key de Resend esté configurada en el servidor.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Account Info */}
            <div className="bg-white border border-[#E5E5E5] rounded-sm p-6">
                <h2 className="font-heading font-bold mb-4">Información de la cuenta</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                        <span className="label-overline">Nombre</span>
                        <p className="mt-1 font-medium">{user?.name}</p>
                    </div>
                    <div>
                        <span className="label-overline">Empresa</span>
                        <p className="mt-1 font-medium">{user?.company || "-"}</p>
                    </div>
                    <div>
                        <span className="label-overline">Email</span>
                        <p className="mt-1 font-medium">{user?.email}</p>
                    </div>
                    <div>
                        <span className="label-overline">Telegram vinculado</span>
                        <p className="mt-1 font-medium">{user?.telegram_chat_id ? "Sí" : "No"}</p>
                    </div>
                    <div>
                        <span className="label-overline">WhatsApp vinculado</span>
                        <p className="mt-1 font-medium">{user?.whatsapp_id ? "Sí" : "No"}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsPage;
